/* eslint-disable react/no-unescaped-entities */
/* eslint-disable camelcase */
import classNames from 'classnames'
import React, { memo, useState } from 'react'
import SlideDown from 'react-slidedown'

import { HandCoinIcon } from '~/assets/icons'
import Trend from '~/assets/icons/trend'
import { useInBucket } from '~/hooks/use-in-bucket'
import smoothScrollTo from '~/utils/smooth-scroll-to'
import { track, trackSegment } from '~/utils/tracking'

import { getProviderPriceRiseContent } from './providers-price-rise-content'
import styles from './styles.module.css'

export interface PriceRiseBannerProps {
  currentProvider?: string
  setFilter: (filter: { [x: string]: string[] }) => void
}

export interface ProviderContent {
  id: string
  major: boolean
  name: string
  logo: string
  synopsis: string
  services: string[]
  connectionTypes: string[]
}

const DISTANCE_TO_TABLE = 300
const TABLE_TOP_OFFSET = 100

const Accordion = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className={styles.accordionItem}>
      <SlideDown transitionOnAppear={false}>{!!isOpen && children}</SlideDown>

      <button
        type='button'
        className={styles.accordionButton}
        aria-expanded={isOpen}
        aria-controls='accordion-panel'
        data-testid='accordion-button'
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{isOpen ? 'Show less' : 'What are my options?'}</span>

        <svg className={classNames(styles.accordionButtonIcon, { [styles.accordionButtonIconClosed]: !isOpen })}>
          <use xlinkHref='/icons.svg#icon-chevron-up_1' />
        </svg>
      </button>
    </div>
  )
}

const PriceRiseBanner: React.FC<PriceRiseBannerProps> = ({ currentProvider, setFilter }) => {
  const handleOnClick = () => {
    trackSegment('Element Clicked', {
      element_name: 'Filter Button',
      element_text: 'View deals',
      element_type: 'button'
    })
    track('Broadband - CityFibre Banner', 'Filter selected')
    setFilter({ specialOffers: ['NO_PRICE_RISE_2024'] })
    smoothScrollTo('#table-header', DISTANCE_TO_TABLE, TABLE_TOP_OFFSET)
  }

  const notPriceRise = useInBucket('experiment-broadband-price-rise-2024', 'no-price-rises')
  if (notPriceRise) return null

  const providerContent = getProviderPriceRiseContent(currentProvider)

  const Title = () => {
    if (!providerContent?.priceIncrease || providerContent.id === null)
      return (
        <h3 className={styles.title}>
          Broadband <u className={styles.underline}>price rises</u>
        </h3>
      )

    return (
      <h3 className={styles.title}>
        Switch before your <u className={styles.underline}>price rises</u>
      </h3>
    )
  }

  const LeadIn = () => {
    const LeadInText = () => {
      if (providerContent.id === null)
        return (
          <span className={styles.subtitle}>
            <Trend className={styles.icon} />
            Your broadband bill may rise&nbsp;<b>from April</b>
          </span>
        )

      if (providerContent?.priceIncrease)
        return (
          <span className={styles.subtitle}>
            <Trend className={styles.icon} />

            <span className={styles.leadInText}>
              Your {providerContent.name} bill will rise by&nbsp;
              <b>
                {providerContent?.priceIncrease}%{providerContent?.average ? ' on average' : ''} from April
              </b>
            </span>
          </span>
        )

      return (
        <span className={styles.subtitle}>
          <HandCoinIcon className={styles.icon} />
          Your {providerContent.name} bill won&lsquo;t rise during your contract
        </span>
      )
    }

    const ProviderLogo = () => {
      if (!providerContent?.logo) return null

      return (
        <span className={styles.logo}>
          <img
            src={`${providerContent.logo}?auto=compress%2C%20format&fit=fill&w=128&h=80&fill=solid&ixlib=react-9.5.1-beta.1`}
            alt={providerContent.name}
            className={styles.image}
          />
        </span>
      )
    }

    return (
      <div className={styles.leadIn}>
        <ProviderLogo />

        <LeadInText />
      </div>
    )
  }

  const Table = () => {
    if (!providerContent?.priceIncrease) return <div className={styles.spacer} />

    const getTablePrices = (increase: number) => {
      const examplePriceOld = 30
      const examplePriceDifference = parseFloat(((increase / 100) * examplePriceOld).toFixed(2))
      const examplePriceNew = examplePriceOld + examplePriceDifference

      return { difference: examplePriceDifference, new: examplePriceNew }
    }

    const { difference: examplePriceDifference, new: examplePriceNew } = getTablePrices(providerContent.priceIncrease)

    return (
      <div className={styles.tableWrapper}>
        {providerContent.tableIntro}

        <p className={styles.tableIntro}>Here&lsquo;s an example of how it could affect your bill:</p>

        <div className={styles.table}>
          <div className={styles.tableColumn}>
            <span className={styles.tableTitle}>Current monthly cost</span>
            <span className={styles.tableText}>£30.00</span>
          </div>

          <div className={styles.tableColumn}>
            <span className={styles.tableTitle}>New monthly cost</span>

            <b className={styles.tableText}>
              {`£${examplePriceNew} `}

              <span className={styles.priceIncrease}>
                <Trend className={styles.iconBadge} />
                {providerContent.priceIncrease}%
              </span>
            </b>
          </div>

          <div className={styles.tableColumn}>
            <span className={styles.tableTitle}>Increase per month</span>
            <span className={styles.tableText}>{`£${examplePriceDifference}`}</span>
          </div>
        </div>
      </div>
    )
  }

  const BottomContent = () => {
    const ReadyToSwitch = () => (
      <div className={styles.readyToSwitch}>
        {providerContent.id ? (
          <>
            <span className={styles.sectionTitle}>Ready to switch?</span>

            <p>
              Price rises also apply out of contract, but if your initial term has ended you can switch to a cheaper
              deal right away.
            </p>
          </>
        ) : (
          <>
            <span className={styles.sectionTitle}>Beat the April price rises</span>

            <p>
              Some providers are freezing their prices until 2025, while some others don't have any mid-contract price
              rises at all.
            </p>
          </>
        )}

        <button className={styles.button} onClick={handleOnClick} onKeyDown={handleOnClick}>
          See our ‘No price rise’ deals
        </button>
      </div>
    )

    const StillInContract = () => {
      if (!providerContent.stillInContract) return null

      return (
        <div className={styles.stillInContract}>
          <span className={styles.sectionTitle}>Still in a contract?</span>

          {providerContent.stillInContract}

          <a
            href={`https://www.uswitch.com/account/reminders/add/?product=BROADBAND&source=price-rise-banner-${currentProvider}`}
            target='_blank'
            rel='noreferrer'
            className={styles.link}
          >
            Sign up for a reminder to find a better deal
          </a>
        </div>
      )
    }

    return (
      <div className={styles.bottomContent}>
        <ReadyToSwitch />

        <StillInContract />
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <Title />

      <LeadIn />

      <Accordion>
        <Table />

        <BottomContent />
      </Accordion>
    </div>
  )
}

const areEqual = (prevProps: PriceRiseBannerProps, nextProps: PriceRiseBannerProps) =>
  JSON.stringify(prevProps.currentProvider) === JSON.stringify(nextProps.currentProvider)

export default memo(PriceRiseBanner, areEqual)
